import { FeatureToggles } from '@/features/feature-toggles'
import DataLoader from 'dataloader'
import { GeneralError, NotFound } from 'fejl'
import { memoize } from '@/utils/memoize-util'
import { orderBy } from 'lodash-es'
import {
  BehaviorSubject,
  firstValueFrom,
  type Observable,
  race,
  Subject,
} from 'rxjs'
import { filter, first, map, switchMap } from 'rxjs/operators'
import {
  type DocumentV1Payload,
  type ZipCreatedV1Event,
  type ZipFailedV1Event,
} from '@taxfyle/documents-api-client'
import { type DocumentResource as DocumentV5Resource } from '@taxfyle/documents-api-client/v5'
import type { GetDocumentDownloadDelegate } from '@/features/shell/platform/types'
import type {
  GetCurrentMemberFn,
  PrimeMemberByPublicIdFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type {
  PrimeDocumentProps,
  PrimeDocumentTypeByShortIdFn,
} from '@/features/documents'
import { collection } from '@/infra/collection'
import type { CursorPaged, Direction } from '@/infra/paging'
import { Id } from '@/infra/id'
import { tapAction, toStream } from '@/utils/mobx-util'
import { getCacheKeyForObject, isNotNil } from '@/utils/object-util'
import { withResolvers } from '@/utils/promise-util'
import { type DocumentsApi } from '@/features/documents/api/documents-api'
import { type DocumentsApiV5 } from '@/features/documents/api/documents-api-v5'
import {
  type DocumentResource,
  type DocumentPopulate,
  type ZipFilesRequest,
} from '@/features/documents/api/resources/document'
import {
  type DocumentsRealtimeMessage,
  type ZipCreatedMessage,
  type ZipFailedMessage,
} from '@/features/documents/api/resources/realtime-messages'
import { v7 } from 'uuid'
import { Document, type DocumentProps } from './Document'
import { type DocumentLinkInput } from './DocumentLinks'
import { type DocumentType } from './DocumentType'
import { type PrimeDocumentTypeProps } from './DocumentTypeStore'
import {
  mapDocumentResourceToProps,
  mapDocumentTypeResourceToProps,
  mapFileDescriptorToDocumentStage,
  mapSearchQueryToListDocumentsRequest,
  mapSearchQueryToSearchDocumentsRequest,
  mapUploadRequestToCreateDocumentRequest,
} from './document-mappers'
import {
  mapDocumentV1PayloadToProps,
  mapDocumentV5ResourceToProps,
  mapSearchDocumentsQueryToSearchRequest,
  mapUploadRequestToDocumentV5Props,
} from './document-mappers-v5'

/**
 * Related information to include when fetching a document.
 */
export interface FetchDocumentOptions {
  history?: boolean
  accesses?: boolean
}

/**
 * Returned by {@link DocumentStore.upload} to observe the upload.
 */
export interface UploadDocumentObserver {
  /**
   * Resolves with the document once it is in the collection, before its
   * contents finish uploading. Stays pending when the upload is discarded
   * or fails before then.
   */
  added: Promise<Document>

  /**
   * Resolves with the document once it exists on the server, so it can be
   * referenced. Stays pending when the upload is discarded or fails before
   * then.
   */
  created: Promise<Document>

  /**
   * Emits the upload progress, starting with nothing uploaded, and
   * completes when the upload settles.
   */
  progress$: Observable<UploadDocumentProgress>

  /**
   * Resolves with the document once its contents are uploaded. Rejects
   * when the upload fails.
   */
  completed: Promise<Document>
}

/**
 * Emitted by {@link UploadDocumentObserver.progress$}.
 */
export interface UploadDocumentProgress {
  /**
   * How much has been uploaded so far in bytes.
   */
  uploaded: number

  /**
   * Total amount to upload in bytes, `0` until known.
   */
  total: number
}

/**
 * Options for {@link DocumentStore.download}.
 */
export interface DownloadDocumentOptions {
  document: Document
  /**
   * Opens the document in the browser instead of downloading it.
   */
  preview: boolean
}

/**
 * The fields the documents can be sorted by.
 */
export type DocumentsSortField = 'Title' | 'DateCreated' | 'DateModified'

/**
 * The filters shared by every search context.
 */
interface SearchDocumentsQueryBase {
  limit?: number
  after?: string | null
  archived?: boolean
  orderBy?: {
    field: DocumentsSortField
    direction: Direction
  }
}

/**
 * Searches the documents linked to the given job.
 */
export interface JobDocumentsQuery extends SearchDocumentsQueryBase {
  context: 'Job'
  jobId: Id
  workspaceId?: Id
  /**
   * Only honored by the v5 API; the legacy API returns every document.
   */
  searchText?: string
  /**
   * Only honored by the v5 API; the legacy API returns every document.
   */
  documentTypeIds?: Id[]
}

/**
 * Searches the documents owned by the current user.
 */
export interface UserDocumentsQuery extends SearchDocumentsQueryBase {
  context: 'User'
  workspaceId?: Id
}

/**
 * Searches documents across workspaces as an admin.
 */
export interface AdminDocumentsQuery extends SearchDocumentsQueryBase {
  context: 'Admin'
  searchText?: string
  ownerPublicIds?: Id[]
  workspaceIds?: Id[]
  jobIds?: Id[]
  documentTypeIds?: Id[]
}

/**
 * Query for searching documents. Owned by the document store — the
 * mappers translate it to the legacy or v5 API request shapes.
 */
export type SearchDocumentsQuery =
  JobDocumentsQuery | UserDocumentsQuery | AdminDocumentsQuery

/**
 * Options for assigning a type to a document.
 */
export interface AssignDocumentTypeOptions {
  /**
   * The document type's short id.
   */
  documentTypeId: string
}

/**
 * Options for updating a document.
 */
export interface UpdateDocumentOptions {
  title?: string
  description?: string
}

/**
 * Options for uploading a document.
 */
export interface UploadDocumentOptions {
  file: File
  workspaceId: Id
  documentTypeId?: string
  /**
   * The job to link the document to. The v5 API requires a link, so
   * linkless uploads stay on the legacy API.
   */
  jobId?: Id
}

/**
 * Manages documents.
 */
export class DocumentStore {
  private readonly _realtimeEventsLegacy$: Observable<DocumentsRealtimeMessage>
  private readonly _manualVerificationRequired$ = new Subject<Document>()
  private readonly _documentAdded$ = new Subject<Document>()
  private readonly _documentsLoader = new DataLoader(
    this._fetchDocumentsByIds.bind(this),
    { cacheKeyFn: getCacheKeyForObject },
  )

  readonly documents = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Document(),
  })

  /**
   * Emits the document whenever manual verification becomes required for it.
   */
  readonly manualVerificationRequired$ =
    this._manualVerificationRequired$.asObservable()

  /**
   * Emits documents that were uploaded or created elsewhere after the
   * collection was loaded.
   */
  readonly documentAdded$ = this._documentAdded$.asObservable()

  /**
   * Emits documents removed from the collection.
   */
  readonly documentRemoved$ = this.documents.removals$

  constructor(
    private readonly _api: DocumentsApi,
    private readonly _apiV5: DocumentsApiV5,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getDocumentDownloadDelegate: GetDocumentDownloadDelegate,
    private readonly _primeMemberByPublicId: PrimeMemberByPublicIdFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeDocumentType: (
      props: PrimeDocumentTypeProps,
    ) => DocumentType,
    private readonly _primeDocumentTypeByShortId: PrimeDocumentTypeByShortIdFn,
  ) {
    this._realtimeEventsLegacy$ = this._api.documents.events()
    this.primeDocument = memoize(this.primeDocument.bind(this), {
      keySelector: ([props]) => props?.id,
    })

    // Both connections receive the same events, so only the one selected
    // by the toggle is connected; flipping the toggle switches over.
    toStream(() => FeatureToggles.documentsApiV5(), true)
      .pipe(
        switchMap((useApiV5) =>
          useApiV5
            ? this._apiV5.events$.pipe(
                tapAction((e) => this._handleRealtimeEventV5(e)),
              )
            : this._realtimeEventsLegacy$.pipe(
                tapAction((e) => this._handleRealtimeEventLegacy(e)),
              ),
        ),
      )
      .subscribe()
  }

  /**
   * Returns the documents linked to the job from the collection, newest
   * first.
   */
  forJob(jobId: Id) {
    const assertedJobId = Id.assert(jobId)
    return orderBy(
      this.documents.filter(
        (doc) => doc.isLinkedToJob(assertedJobId) && !doc.ephemeral,
      ),
      'dateCreated',
      'desc',
    )
  }

  /**
   * Primes a document in the collection and refreshes it in the
   * background.
   */
  primeDocument(props: PrimeDocumentProps) {
    if (!props?.id) {
      return undefined
    }

    const doc =
      this.documents.get(props.id) ?? this.documents.create({ id: props.id })

    this._refetchDocument(props.id).catch(() => undefined)

    return doc
  }

  /**
   * Fetches the document given a ID.
   */
  async fetchDocument(id: Id, options?: FetchDocumentOptions) {
    const populate: DocumentPopulate = { ...options }
    return this._documentsLoader.load([Id.assert(id), populate])
  }

  /**
   * Searches the documents given the query.
   */
  async searchDocuments(
    query: SearchDocumentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Document>> {
    if (FeatureToggles.documentsApiV5()) {
      return this._searchDocumentsV5(query, signal)
    }

    return query.context === 'Admin'
      ? this._searchDocumentsLegacy(query, signal)
      : this._findDocumentsLegacy(query, signal)
  }

  /**
   * Finds the documents using the legacy API.
   */
  private async _findDocumentsLegacy(
    query: JobDocumentsQuery | UserDocumentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Document>> {
    const response = await this._api.documents.list(
      {
        ...mapSearchQueryToListDocumentsRequest(query),
        accesses: true,
        history: true,
      },
      signal,
    )

    return {
      next: response.cursor,
      total: response.total,
      data: this.documents.set(
        response.data.map((r) => this._mapDocumentResourceToProps(r)),
      ),
    }
  }

  /**
   * Searches the documents using the legacy API.
   */
  private async _searchDocumentsLegacy(
    query: AdminDocumentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Document>> {
    const response = await this._api.documents.search(
      mapSearchQueryToSearchDocumentsRequest(query),
      signal,
    )

    return {
      next: response.cursor,
      total: response.total,
      data: this.documents.set(
        response.data.map((r) => this._mapDocumentResourceToProps(r)),
      ),
    }
  }

  /**
   * Searches the documents using the v5 API.
   */
  private async _searchDocumentsV5(
    query: SearchDocumentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Document>> {
    const response = await this._apiV5.documents.search(
      { query: mapSearchDocumentsQueryToSearchRequest(query) },
      { signal },
    )

    return {
      next: response.cursor,
      total: response.total,
      data: response.data.map((resource) => this._setDocumentV5(resource)),
    }
  }

  /**
   * Uploads a document. The v5 API requires a link, so linkless uploads
   * stay on the legacy API.
   *
   * @param request The document to upload.
   * @param signal When already aborted by the time the document is
   *  created, the document is discarded instead of uploaded.
   */
  upload(
    request: UploadDocumentOptions,
    signal?: AbortSignal,
  ): UploadDocumentObserver {
    const progress$ = new BehaviorSubject<UploadDocumentProgress>({
      uploaded: 0,
      total: 0,
    })
    const added = withResolvers<Document>()
    const created = withResolvers<Document>()
    const upload: PendingDocumentUpload = {
      request,
      signal,
      progress$,
      onAdded: added.resolve,
      onCreated: created.resolve,
    }

    const completed =
      FeatureToggles.documentsApiV5() && request.jobId
        ? this._uploadV5(upload)
        : this._uploadLegacy(upload)
    const complete = () => progress$.complete()
    completed.then(complete, complete)

    return {
      added: added.promise,
      created: created.promise,
      progress$: progress$.asObservable(),
      completed,
    }
  }

  /**
   * Uploads a document using the v5 API, which creates and uploads it in
   * a single request.
   */
  private async _uploadV5({
    request,
    signal,
    progress$,
    onAdded,
    onCreated,
  }: PendingDocumentUpload): Promise<Document> {
    const documentId = Id.assert(v7())

    const document = this.documents.set({
      ...mapUploadRequestToDocumentV5Props(request, documentId, new Date()),
      workspace: this._primeWorkspace({ id: request.workspaceId }),
      owner: this._getCurrentMember(request.workspaceId),
    })

    if (signal?.aborted) {
      this.documents.remove(documentId)
      return document
    }

    this._documentAdded$.next(document)
    onAdded(document)

    await this._apiV5.documents
      .upload(
        {
          document_id: documentId,
          workspace_id: Id.assert(request.workspaceId),
          document_type_id: request.documentTypeId,
          link: JSON.stringify({
            type: 'JOB',
            job_id: Id.assert(request.jobId),
          }),
          file: [request.file],
        },
        {
          onProgress: (e) =>
            progress$.next({ uploaded: e.loaded, total: e.total }),
        },
      )
      .catch((err: unknown) => {
        this.documents.remove(documentId)
        throw err
      })

    // The v5 API only creates the document with the upload request, so
    // anything referencing the document must wait for it.
    onCreated(document)

    return (await this._refetchDocument(documentId)) ?? document
  }

  /**
   * Uploads a document using the legacy API, by creating the document
   * and its first version as separate calls.
   */
  private async _uploadLegacy({
    request,
    signal,
    progress$,
    onAdded,
    onCreated,
  }: PendingDocumentUpload): Promise<Document> {
    const resource = await this._api.documents.create(
      mapUploadRequestToCreateDocumentRequest(request),
    )

    const document = this._setDocumentLegacy(resource)
    const id = Id.assert(document.id)

    if (signal?.aborted) {
      await this.archive(document)
      return document
    }

    this._documentAdded$.next(document)
    onAdded(document)
    onCreated(document)

    const version = await this._api.versions.create(
      id,
      {
        message: 'Initial version',
        action: { type: 'ReplaceContents', files: [request.file] },
      },
      {
        onProgress: (e) =>
          progress$.next({ uploaded: e.loaded, total: e.total }),
      },
    )

    document.set({ stage: mapFileDescriptorToDocumentStage(version.files[0]) })
    return document
  }

  /**
   * Updates a document. The v5 API has no update endpoint, so this
   * always uses the legacy API.
   */
  async updateDocument(id: Id, options: UpdateDocumentOptions) {
    const updated = await this._api.documents.update(Id.assert(id), {
      title: options.title,
      description: options.description,
    })
    return this._setDocumentLegacy(updated)
  }

  /**
   * Archives a document. The document is refetched so its retention
   * reflects the archival.
   */
  async archive(document: Document) {
    const id = Id.assert(document.id)
    if (FeatureToggles.documentsApiV5()) {
      await this._apiV5.documents.archive({ id })
    } else {
      await this._api.documents.archive(id)
    }

    return this._refetchDocument(id)
  }

  /**
   * Assigns the document type. The responses don't carry the document,
   * so it is refetched.
   */
  async assignType(id: Id, options: AssignDocumentTypeOptions) {
    const documentId = Id.assert(id)
    if (FeatureToggles.documentsApiV5()) {
      await this._apiV5.documents.assignType({
        id: documentId,
        payload: { document_type_id: options.documentTypeId },
      })
    } else {
      await this._api.documents.assignType(documentId, {
        type: options.documentTypeId,
      })
    }

    return this._refetchDocument(documentId)
  }

  /**
   * Downloads all the specified documents as a zip file.
   */
  async downloadAll(ids: Id[]) {
    return FeatureToggles.documentsApiV5()
      ? this._downloadAllV5(ids)
      : this._downloadAllLegacy(ids)
  }

  /**
   * Downloads all the specified documents as a zip file using the
   * v5 API. The zip is built in the background and its signed URL
   * arrives on the realtime connection.
   */
  private async _downloadAllV5(ids: Id[]) {
    const { work_id: workId } = await this._apiV5.documents.zipFiles({
      payload: { document_ids: ids.map((id) => Id.assert(id)) },
    })

    const created$ = this._apiV5.events$.pipe(
      filter(
        (e): e is ZipCreatedV1Event => e.type === 'documents.v1.zip-created',
      ),
      first((e) => e.data.zip_request_id === workId),
      map((e) => e.data.url),
    )

    const failed$ = this._apiV5.events$.pipe(
      filter(
        (e): e is ZipFailedV1Event => e.type === 'documents.v1.zip-failed',
      ),
      first((e) => e.data.zip_request_id === workId),
      map((e): string => {
        throw new GeneralError(e.data.message)
      }),
    )

    const url = await firstValueFrom(race(created$, failed$))
    this._getDocumentDownloadDelegate().download(url, false)
  }

  /**
   * Downloads all the specified documents as a zip file using the
   * legacy API, resolving each document's version and file ids first.
   */
  private async _downloadAllLegacy(ids: Id[]) {
    const docs = (
      await Promise.all(
        ids.map(async (id) => {
          const resource = await this._api.documents.get(Id.assert(id), {
            history: true,
          })

          return resource
            ? {
                document_id: resource.id,
                file_id: resource.latest_version.files[0].id,
                version_id: resource.latest_version.id,
              }
            : null
        }),
      )
    ).filter(isNotNil) satisfies ZipFilesRequest

    const ephemeral = await this._api.documents.zipFiles(docs)

    const created$ = this._realtimeEventsLegacy$.pipe(
      filter((e): e is ZipCreatedMessage => e.type === 'ZipCreated'),
      first((msg) => msg.data.document_id === ephemeral.id),
    )

    const failed$ = this._realtimeEventsLegacy$.pipe(
      filter((e): e is ZipFailedMessage => e.type === 'ZipFailed'),
      first((msg) => msg.data.document_id === ephemeral.id),
      map((msg) => {
        throw new GeneralError(msg.data.reason)
      }),
    )

    return firstValueFrom(
      race(created$, failed$).pipe(
        switchMap(() =>
          this.fetchDocument(ephemeral.long_id, {
            accesses: true,
            history: true,
          }),
        ),
        // SAFETY: the zip-created event means the ephemeral document exists.
        switchMap((doc) => this.download({ document: doc!, preview: false })),
      ),
    )
  }

  /**
   * Downloads the document via a signed URL handed to the platform delegate.
   */
  async download({ document, preview }: DownloadDocumentOptions) {
    const downloadDelegate = this._getDocumentDownloadDelegate()
    const id = Id.assert(document.id)
    const response = await (FeatureToggles.documentsApiV5()
      ? this._apiV5.documents.download({ id, preview })
      : this._api.documents.getDownloadUrl(id, preview))

    downloadDelegate.download(response.url, preview)
  }

  /**
   * Links the document, unless it already has the link.
   */
  async createDocumentLink(document: Document, link: DocumentLinkInput) {
    if (document.hasLink(link)) {
      return
    }

    const id = Id.assert(document.id)
    const jobId = Id.assert(link.jobId)
    if (FeatureToggles.documentsApiV5()) {
      await this._apiV5.documents.link({
        id,
        payload: { type: 'JOB', job_id: jobId },
      })
    } else {
      await this._api.accesses.create({
        document_id: id,
        access: { type: 'Job', job_id: jobId },
      })
    }

    await this._refetchDocument(id)
  }

  /**
   * Removes the link from the document.
   */
  async removeDocumentLink(document: Document, link: DocumentLinkInput) {
    if (!document.hasLink(link)) {
      throw new Error(
        'Cannot unlink the document because the link was not found',
      )
    }

    return FeatureToggles.documentsApiV5()
      ? this._removeDocumentLinkV5(document, link)
      : this._removeDocumentLinkLegacy(document, link)
  }

  /**
   * Removes the link from the document using the v5 API.
   */
  private async _removeDocumentLinkV5(
    document: Document,
    link: DocumentLinkInput,
  ) {
    const id = Id.assert(document.id)
    await this._apiV5.documents.unlink({
      id,
      payload: { type: 'JOB', job_id: Id.assert(link.jobId) },
    })

    await this._refetchDocument(id)
  }

  /**
   * Removes the link from the document using the legacy API. Links only
   * carry job ids, so the backing access id is resolved by refetching the
   * accesses.
   */
  private async _removeDocumentLinkLegacy(
    document: Document,
    link: DocumentLinkInput,
  ) {
    const id = Id.assert(document.id)
    const resource = await this._api.documents.get(id, { accesses: true })
    const access = resource?.accesses?.find(
      (a) => a.type === 'Job' && a.job_id === link.jobId,
    )

    if (!access) {
      throw new Error(
        'Cannot unlink the document because the link was not found',
      )
    }

    await this._api.accesses.delete(access.id)
    await this._refetchDocument(id)
  }

  /**
   * Confirms document for the given job.
   */
  async confirmDocument(documentLongId: Id, jobId: Id) {
    return FeatureToggles.documentsApiV5()
      ? this._confirmDocumentV5(documentLongId)
      : this._confirmDocumentLegacy(documentLongId, jobId)
  }

  /**
   * Confirms the document using the v5 API, which attests it by its
   * id alone.
   */
  private async _confirmDocumentV5(documentLongId: Id) {
    await this._apiV5.documents.attest({ id: Id.assert(documentLongId) })
    await this._refetchDocument(documentLongId)
  }

  /**
   * Confirms the document using the legacy API. The latest version and
   * file ids are resolved by refetching since the client doesn't track
   * versions.
   */
  private async _confirmDocumentLegacy(documentLongId: Id, jobId: Id) {
    const resource = await this._api.documents.get(Id.assert(documentLongId), {
      history: true,
    })

    const latestVersion = resource?.latest_version
    const fileId = latestVersion?.files[0]?.id

    if (!latestVersion || !fileId) {
      throw new Error(
        'Cannot confirm the document because its latest version could not be resolved',
      )
    }

    await this._api.clientDataVerification.confirmFile(
      Id.assert(documentLongId),
      Id.assert(latestVersion.id),
      Id.assert(fileId),
      { job_id: Id.assert(jobId) },
    )

    await this._refetchDocument(documentLongId)
  }

  /**
   * Refetches the document, bypassing the documents loader cache,
   * and returns the document from the collection.
   */
  private async _refetchDocument(id: Id) {
    const key: [string, DocumentPopulate] = [
      Id.assert(id),
      { accesses: true, history: true },
    ]
    const document = await this._documentsLoader.clear(key).load(key)
    return document ?? undefined
  }

  /**
   * Fetches the documents given the ids.
   */
  private async _fetchDocumentsByIds(
    ids: readonly [string, DocumentPopulate][],
  ) {
    return FeatureToggles.documentsApiV5()
      ? this._fetchDocumentsByIdsV5(ids)
      : this._fetchDocumentsByIdsLegacy(ids)
  }

  /**
   * Fetches the documents given the ids using the v5 API.
   */
  private async _fetchDocumentsByIdsV5(
    ids: readonly [string, DocumentPopulate][],
  ) {
    const resources = await Promise.all(
      ids.map(([id]) =>
        this._apiV5.documents.get({ id }).catch(NotFound.ignore(null)),
      ),
    )

    return resources.map((r) => (r ? this._setDocumentV5(r) : null))
  }

  /**
   * Fetches the documents given the ids using the legacy API.
   */
  private async _fetchDocumentsByIdsLegacy(
    ids: readonly [string, DocumentPopulate][],
  ) {
    const resources = await Promise.all(
      ids.map(([id, populate]) =>
        this._api.documents
          .get(id, { ...populate })
          .catch(NotFound.ignore(null)),
      ),
    )

    return resources.map((r) => (r ? this._setDocumentLegacy(r) : null))
  }

  /**
   * Sets a legacy document resource into the collection.
   */
  private _setDocumentLegacy(resource: DocumentResource) {
    return this.documents.set(this._mapDocumentResourceToProps(resource))
  }

  /**
   * Sets a v5 document resource into the collection.
   */
  private _setDocumentV5(resource: DocumentV5Resource) {
    return this.documents.set(this._mapDocumentV5ResourceToProps(resource))
  }

  /**
   * Maps a legacy document resource to document props, priming the models
   * it refers to.
   */
  private _mapDocumentResourceToProps(
    resource: DocumentResource,
  ): Partial<DocumentProps> {
    return {
      ...mapDocumentResourceToProps(resource),
      owner: this._primeMemberByPublicId({
        userPublicId: resource.owner_public_id,
        workspaceId: resource.workspace_id,
      }),
      author: this._primeMemberByPublicId({
        userPublicId: resource.latest_version?.author_public_id,
        workspaceId: resource.workspace_id,
      }),
      documentType:
        resource.document_type &&
        this._primeDocumentType(
          mapDocumentTypeResourceToProps(resource.document_type),
        ),
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
    }
  }

  /**
   * Maps a v5 document resource to document props, priming the models it
   * refers to.
   */
  private _mapDocumentV5ResourceToProps(
    resource: DocumentV5Resource,
  ): Partial<DocumentProps> {
    return {
      ...mapDocumentV5ResourceToProps(resource),
      owner: this._primeMemberByPublicId({
        userPublicId: resource.owner_id,
        workspaceId: resource.workspace_id,
      }),
      author: this._primeMemberByPublicId({
        userPublicId: resource.author_id,
        workspaceId: resource.workspace_id,
      }),
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
      // The v5 API identifies document types by their short id.
      documentType: resource.document_type
        ? this._primeDocumentTypeByShortId(
            resource.document_type.id,
            resource.document_type.name,
          )
        : null,
    }
  }

  /**
   * Maps a v1 realtime document payload to document props, priming the
   * models it refers to.
   */
  private _mapDocumentV1PayloadToProps(
    payload: DocumentV1Payload,
  ): Partial<DocumentProps> {
    return {
      ...mapDocumentV1PayloadToProps(payload),
      owner: this._primeMemberByPublicId({
        userPublicId: payload.owner_public_id,
        workspaceId: payload.workspace_id,
      }),
      author: this._primeMemberByPublicId({
        userPublicId: payload.author_id,
        workspaceId: payload.workspace_id,
      }),
      workspace: this._primeWorkspace({ id: payload.workspace_id }),
      // The payload always states its type, so a missing one genuinely
      // means untyped and clears it. The v5 API identifies document
      // types by their short id.
      documentType: payload.document_type
        ? this._primeDocumentTypeByShortId(
            payload.document_type.id,
            payload.document_type.name,
          )
        : null,
    }
  }

  /**
   * Handles the v1 realtime events emitted by the v5 API.
   */
  private _handleRealtimeEventV5(e: DocumentsRealtimeMessage) {
    switch (e.type) {
      case 'documents.v1.created':
      case 'documents.v1.updated': {
        // Archived updates only patch documents still in the collection,
        // so locally discarded documents don't get resurrected.
        const existing = this.documents.get(e.data.id)
        if (e.data.archived && !existing) {
          break
        }

        const document = this.documents.set(
          this._mapDocumentV1PayloadToProps(e.data),
        )

        if (!existing) {
          this._documentAdded$.next(document)
        }

        if (!document.archived && document.isManualAttestationRequired) {
          this._manualVerificationRequired$.next(document)
        }
        break
      }

      case 'documents.v1.unlinked': {
        const link = e.data.link
        if (link.type !== 'JOB') {
          break
        }

        this.documents
          .get(e.data.id)
          ?.removeLink({ type: 'Job', jobId: Id(link.job_id) })
        break
      }
    }
  }

  /**
   * Handles realtime events emitted by the legacy API.
   */
  private _handleRealtimeEventLegacy(e: DocumentsRealtimeMessage) {
    switch (e.type) {
      case 'DocumentCreated':
      case 'DocumentUpdated': {
        // Archived updates only patch documents still in the collection,
        // so locally discarded documents don't get resurrected.
        const existing = this.documents.get(e.data.long_id)
        if (e.data.archived && !existing) {
          break
        }

        const document = this._setDocumentLegacy(e.data)
        if (!existing) {
          this._documentAdded$.next(document)
        }
        break
      }

      // The document's stage is derived from its latest version's file
      // state, so version events must patch it on the document.
      case 'VersionScanned':
      case 'ClientDataVerified':
        this.documents.get(e.data.document_long_id)?.set({
          stage: mapFileDescriptorToDocumentStage(e.data.files[0]),
        })
        break
      case 'ClientDataManualVerificationRequired': {
        const document = this.documents.get(e.data.document_long_id)
        document?.set({
          stage: mapFileDescriptorToDocumentStage(e.data.files[0]),
        })

        if (document && !document.archived) {
          this._manualVerificationRequired$.next(document)
        }
        break
      }

      // The document's links and edit rights are derived from its
      // accesses, so access events must refresh the document.
      case 'AccessCreated':
      case 'AccessUpdated':
      case 'AccessDeleted':
        this._refetchDocument(e.data.document_long_id).catch(() => undefined)
        break
    }
  }
}

/**
 * An upload started by {@link DocumentStore.upload}.
 */
interface PendingDocumentUpload {
  request: UploadDocumentOptions
  signal?: AbortSignal
  progress$: BehaviorSubject<UploadDocumentProgress>
  onAdded: (document: Document) => void
  onCreated: (document: Document) => void
}
